import { createSlice, isAnyOf } from '@reduxjs/toolkit'
import { Company } from '@leadgen/models/client'
import authActions from '../auth/actions'
import actions from './actions'

export interface CompanyState {
  company?: Company
  syncStartTime: string | null
  syncClientSecret: string | null
  syncIntentType: 'payment' | 'setup' | null
  subscriptionAwaitingSync: boolean
  paymentAwaitingSync: boolean
}

const initialState: CompanyState = {
  company: undefined,
  syncStartTime: null,
  syncClientSecret: null,
  syncIntentType: null,
  subscriptionAwaitingSync: false,
  paymentAwaitingSync: false,
}

export const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(authActions.loggedOut.type, () => {
        return initialState
      })
      .addCase(actions.updateCompanyMonthlyCreditLimit, (state, action) => {
        if (state.company) {
          state.company.monthlyCreditLimit = action.payload
        }
      })
      .addCase(actions.switchCard, (state, action) => {
        if (state.company) {
          state.company.stripePaymentMethodId = action.payload.companyCardId
        }
      })
      .addCase(actions.cancelSubscription, state => {
        if (state.company) {
          state.company.stripePaymentMethodId = null
        }
        state.subscriptionAwaitingSync = false
        state.paymentAwaitingSync = false
      })
      .addCase(actions.topup, (state, action) => {
        state.syncClientSecret = action.payload.clientSecret
        state.syncIntentType = action.payload.clientSecret ? 'payment' : null
        state.syncStartTime = action.payload.clientSecret ? new Date().toISOString() : null
        state.paymentAwaitingSync = true
      })
      .addCase(actions.updateLastChangedTemplate, (state, action) => {
        if (state.company) {
          state.company.lastChangedTemplateVersion = action.payload.lastChangedTemplateVersion
        }
      })
      .addCase(actions.purchaseSubscription, (state, action) => {
        if (action.payload.paymentMethodId) {
          if (state.company) {
            state.company.stripePaymentMethodId = action.payload.paymentMethodId
          }
        } else {
          state.syncClientSecret = action.payload.clientSecret
          state.syncIntentType = action.payload.clientSecret ? action.payload.intentType : null
          state.syncStartTime = action.payload.clientSecret ? new Date().toISOString() : null
          state.subscriptionAwaitingSync = true
          if (
            state.company?.monthlyCreditLimit &&
            state.company.monthlyCreditLimit > state.company.credits
          ) {
            state.paymentAwaitingSync = true
          }
        }
      })
      .addCase(actions.paymentSyncFailed, state => {
        state.syncStartTime = null
        state.syncIntentType = null
        state.syncClientSecret = null
        state.subscriptionAwaitingSync = false
        state.paymentAwaitingSync = false
      })
      .addMatcher(isAnyOf(actions.updateCompany, actions.companyLoaded), (state, action) => {
        state.company = action.payload
        if (state.company.stripePaymentMethodId) {
          state.syncStartTime = null
          state.syncIntentType = null
          state.syncClientSecret = null
          state.subscriptionAwaitingSync = false
        }
        if (
          state.company.stripePaymentMethodId &&
          state.company.creditsPurchasedMonthlyCreditLimit === state.company.monthlyCreditLimit
        ) {
          state.syncStartTime = null
          state.syncIntentType = null
          state.syncClientSecret = null
          state.paymentAwaitingSync = false
        }
      })
  },
})

export default companySlice
